import React, { useState, useEffect } from 'react';
import { useCustomerIdentity } from '../context/CustomerIdentityContext';

const benefitLabel = (identity) => {
  if (identity?.benefitType === 'cortesia') return 'Cortesía · combo gratis';
  if (identity?.benefitType === 'descuento') return 'Precio de empleado en combos';
  return null;
};

const IdentifyModal = ({ isOpen, onClose }) => {
  const { identity, identify, clearIdentity, isIdentifying, error, hasBenefit, benefitRemaining } = useCustomerIdentity();
  const [phone, setPhone] = useState('');

  useEffect(() => {
    if (isOpen) setPhone('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await identify(phone.replace(/\D/g, ''));
    if (ok) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-3xl border border-white/10 bg-[#12121a]/95 backdrop-blur-xl p-5"
        role="dialog"
        aria-modal="true"
        aria-label="Identificación de cliente"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="absolute top-3 right-3 w-9 h-9 rounded-full grid place-items-center text-white/70 hover:text-white hover:bg-white/10 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-300/70"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {identity ? (
          <div>
            <p className="text-amber-300 text-[11px] font-semibold uppercase tracking-wider">Identificado</p>
            <h2 className="text-white text-lg font-bold leading-tight">Hola, {identity.name || 'cliente'}</h2>
            {hasBenefit && (
              <div className="mt-3 rounded-2xl bg-amber-300/10 border border-amber-300/30 p-3">
                <p className="text-amber-200 text-sm font-semibold">{benefitLabel(identity)}</p>
                <p className="text-white/60 text-xs mt-1">
                  {benefitRemaining === Infinity
                    ? 'Disponible hoy'
                    : (benefitRemaining > 0
                      ? `Disponible hoy: ${benefitRemaining} combo${benefitRemaining === 1 ? '' : 's'}`
                      : 'Ya lo usaste hoy — vuelve mañana')}
                </p>
              </div>
            )}
            {identity.coupons && identity.coupons.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {identity.coupons.map((c) => (
                  <div key={c.id} className="rounded-2xl bg-amber-300/10 border border-amber-300/30 p-3">
                    <p className="text-amber-200 text-sm font-semibold">
                      Cupón · {c.reward_type === 'percent' ? `${c.reward_value}% de descuento` : `$${c.reward_value} de descuento`}
                    </p>
                    {(c.description || c.expires_at) && (
                      <p className="text-white/60 text-xs mt-1">
                        {c.description || ''}{c.description && c.expires_at ? ' · ' : ''}{c.expires_at ? `vence ${c.expires_at}` : ''}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
            {!hasBenefit && !(identity.coupons && identity.coupons.length > 0) && (
              <p className="text-white/50 text-sm mt-2">No tienes beneficios activos por ahora.</p>
            )}
            <button
              type="button"
              onClick={() => { clearIdentity(); onClose(); }}
              className="mt-4 w-full py-2.5 rounded-xl border border-white/15 text-white/80 hover:bg-white/10 transition-colors cursor-pointer text-sm font-medium"
            >
              No soy yo / Salir
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p className="text-amber-300 text-[11px] font-semibold uppercase tracking-wider">¿Ya eres cliente?</p>
            <h2 className="text-white text-lg font-bold leading-tight">Identifícate</h2>
            <p className="text-white/50 text-xs mt-1 mb-3">
              Pon tu teléfono para ver tus beneficios (precio de empleado, cupones) antes de pedir.
            </p>
            <label htmlFor="identify-phone" className="sr-only">Teléfono</label>
            <input
              id="identify-phone"
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Teléfono (10 dígitos)"
              maxLength={10}
              className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-amber-300/70"
            />
            {error && <p className="text-red-300 text-xs mt-2">{error}</p>}
            <button
              type="submit"
              disabled={isIdentifying || phone.replace(/\D/g, '').length < 10}
              className="mt-3 w-full py-2.5 rounded-xl bg-amber-300 text-[#12121a] font-bold disabled:opacity-50 transition-opacity cursor-pointer"
            >
              {isIdentifying ? 'Verificando…' : 'Ver mis beneficios'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 w-full py-2 text-white/50 hover:text-white/80 text-sm transition-colors cursor-pointer"
            >
              Entrar como invitado
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default IdentifyModal;
